import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Button, Grid, Tabs, Tab,
  CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Snackbar, IconButton
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import SaveIcon from '@mui/icons-material/Save';

import { LOYALTY_API_BASE, SECTION_COLORS } from './constants';
import { loyaltyFetch } from './hooks/loyaltyFetch';
import { useBrand } from '../../contexts/BrandContext';
import { useCustomerEntitlementsCatalog } from './hooks/useCustomerEntitlementsCatalog';
import { loyaltyApiDetail } from './utils/loyaltyApiMessage';
import {
  looksLikeEmail,
  resolveEmailFromUnomiProfile,
  resolveRouteProfileId,
} from './utils/customerLookup';
import CustomerCouponsEntitlementsTable from './components/shared/CustomerCouponsEntitlementsTable';
import EntitlementsHistoryPanel from './components/shared/EntitlementsHistoryPanel';
import SaleTransactionsPanel from './components/shared/SaleTransactionsPanel';
import CustomerValidityDatesPanel from './components/shared/CustomerValidityDatesPanel';
import PointMovementsTable from './components/shared/PointMovementsTable';
import PurchaseMovementsTable from './components/shared/PurchaseMovementsTable';
import LoyaltyStatusOverview from './components/shared/LoyaltyStatusOverview';
import LoyaltyHistoryPanel from './components/shared/LoyaltyHistoryPanel';
import { isPurchasesMode } from './utils/loyaltyQualification';
import { usePostPurchasePolling } from './hooks/usePostPurchasePolling';
import {
  fetchCustomerLoyaltyBundle,
  buildCustomerPath,
  loyaltyBrandHeaders,
  normalizeLoyaltyBrand,
  getMergeNoticeMessage,
  shouldShowMergeNotice,
} from './utils/loyaltyCustomerApi';

export default function ProfilFidelite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get('email') || '';
  const { id: routeProfileId } = useParams();
  const { currentBrand } = useBrand();
  const brandName = currentBrand?.name || '';
  const { statusLabels: catalogStatusLabels } = useCustomerEntitlementsCatalog(brandName);

  const [resolvedProfileId, setResolvedProfileId] = useState('');
  const [resolvingProfileId, setResolvingProfileId] = useState(true);
  const profileId = resolvedProfileId || routeProfileId || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [loyalty, setLoyalty] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [movements, setMovements] = useState([]);
  const [purchaseMovements, setPurchaseMovements] = useState([]);
  const [customerCouponsWithRewards, setCustomerCouponsWithRewards] = useState([]);
  const [rewardDefs, setRewardDefs] = useState({});
  const [dataRefreshToken, setDataRefreshToken] = useState(0);
  const [customerEmail, setCustomerEmail] = useState(emailFromQuery);
  const [loadErrors, setLoadErrors] = useState({});
  const [reconciliation, setReconciliation] = useState(null);

  useEffect(() => {
    if (emailFromQuery) setCustomerEmail(emailFromQuery);
  }, [emailFromQuery]);

  const fetchAllData = useCallback(async () => {
    if (!brandName || !profileId) return;
    setLoading(true);
    setError(null);
    setLoadErrors({});
    setReconciliation(null);
    let email = String(customerEmail || emailFromQuery || "").trim();
    if (!email && profileId && !looksLikeEmail(profileId)) {
      const fromUnomi = await resolveEmailFromUnomiProfile(profileId, brandName);
      if (fromUnomi) {
        email = fromUnomi;
        setCustomerEmail(fromUnomi);
      }
    }

    try {
      const bundle = await fetchCustomerLoyaltyBundle(brandName, profileId, email);
      if (!bundle.ok) {
        if (bundle.status === 404) {
          throw new Error(
            email
              ? "Profil client introuvable dans la fidélité pour cet email / profileId."
              : "Profil client introuvable. Ajoutez ?email=… à l'URL ou recherchez par email.",
          );
        }
        throw new Error(bundle.fatalError || "Erreur lors du chargement du profil fidélité.");
      }

      setLoyalty(bundle.loyalty);
      setMovements(bundle.movements);
      setPurchaseMovements(bundle.purchaseMovements || []);
      setCustomerCouponsWithRewards(bundle.couponsWithRewards);
      setLoadErrors(bundle.errors || {});
      setReconciliation(bundle.reconciliation || null);

      const apiEmail = String(
        bundle.customer?.email || bundle.loyalty?.email || email || "",
      ).trim();
      if (apiEmail) setCustomerEmail(apiEmail);

      const viewProfileId = bundle.viewProfileId || profileId;
      const customerData = bundle.customer || {
        profile_id: viewProfileId,
        points_balance: bundle.loyalty?.pointsBalance ?? 0,
        points_expires_at: bundle.loyalty?.pointsExpiresAt ?? null,
        loyalty_status: bundle.loyalty?.loyaltyStatus ?? null,
        loyalty_status_expires_at: bundle.loyalty?.statusExpiresAt ?? null,
      };
      setCustomer(customerData);

      const items = bundle.couponsWithRewards;
      const rewardIds = new Set();
      items.forEach((it) => {
        const rewards = Array.isArray(it?.rewards) ? it.rewards : [];
        rewards.forEach((r) => { if (r?.reward_id) rewardIds.add(String(r.reward_id)); });
      });
      if (rewardIds.size > 0) {
        const brand = normalizeLoyaltyBrand(brandName);
        const headers = loyaltyBrandHeaders(brand);
        const rRes = await loyaltyFetch(`${LOYALTY_API_BASE}/rewards?brand=${encodeURIComponent(brand)}`, { headers });
        if (rRes.ok) {
          const rData = await rRes.json();
          const defMap = {};
          rData.forEach(r => { defMap[r.id] = r; });
          setRewardDefs(defMap);
        }
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [brandName, profileId, customerEmail, emailFromQuery, navigate]);

  useEffect(() => {
    let cancelled = false;

    const resolveId = async () => {
      const raw = String(routeProfileId || "").trim();
      if (!raw) {
        setResolvedProfileId("");
        setResolvingProfileId(false);
        return;
      }

      setResolvingProfileId(true);
      setError(null);

      if (!looksLikeEmail(raw)) {
        setResolvedProfileId(raw);
        setResolvingProfileId(false);
        return;
      }

      try {
        const profileIdFromEmail = await resolveRouteProfileId(raw);
        if (cancelled) return;
        if (profileIdFromEmail && profileIdFromEmail !== raw) {
          navigate(`/fidelite/profil/${encodeURIComponent(profileIdFromEmail)}`, { replace: true });
          return;
        }
        setResolvedProfileId(profileIdFromEmail || raw);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Impossible de résoudre cet email vers un Profile ID Unomi.");
          setResolvedProfileId("");
        }
      } finally {
        if (!cancelled) setResolvingProfileId(false);
      }
    };

    resolveId();
    return () => { cancelled = true; };
  }, [routeProfileId, navigate]);

  useEffect(() => {
    if (resolvingProfileId || !profileId) return;
    fetchAllData();
  }, [fetchAllData, resolvingProfileId, profileId]);

  usePostPurchasePolling({
    brandName,
    profileId,
    enabled: Boolean(brandName && profileId && !resolvingProfileId),
    onPoll: () => {
      fetchAllData();
      setDataRefreshToken((t) => t + 1);
    },
  });

  // ── Admin actions (update values) ─────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [tierOpen, setTierOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const initialBirthdate = useMemo(() => {
    const bd = customer?.birthdate;
    if (!bd || typeof bd !== 'string') return '';
    return (bd.length === 10 && bd[4] === '-' && bd[7] === '-') ? bd : '';
  }, [customer?.birthdate]);

  const [editForm, setEditForm] = useState({ gender: '', birthdate: '' });

  const openEdit = () => {
    setActionError(null);
    setEditForm({
      gender: customer?.gender || '',
      birthdate: initialBirthdate,
    });
    setEditOpen(true);
  };

  const tiers = useMemo(() => Array.isArray(loyalty?.tiers) ? loyalty.tiers : [], [loyalty?.tiers]);
  const [tierKey, setTierKey] = useState('');
  const [tierReason, setTierReason] = useState('');

  const [couponActionId, setCouponActionId] = useState(null);
  const [entitlementsTab, setEntitlementsTab] = useState(0);

  const openTier = () => {
    setActionError(null);
    setTierKey(loyalty?.currentTier?.key || loyalty?.loyaltyStatus || '');
    setTierReason('');
    setTierOpen(true);
  };

  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const handleUpsertCustomer = async () => {
    if (!brandName || !profileId) return;
    setSaving(true);
    setActionError(null);
    const brand = normalizeLoyaltyBrand(brandName);
    const email = String(customerEmail || emailFromQuery || "").trim();
    try {
      const payload = {
        brand,
        profileId,
        gender: editForm.gender || null,
        birthdate: editForm.birthdate || null,
      };
      const res = await loyaltyFetch(`${LOYALTY_API_BASE}/customers/upsert`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...loyaltyBrandHeaders(brand),
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || `Erreur HTTP ${res.status}`);
      setEditOpen(false);
      notify("Profil client mis à jour", "success");
      await fetchAllData();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetTier = async () => {
    if (!brandName || !profileId || !tierKey) return;
    setSaving(true);
    setActionError(null);
    const brand = normalizeLoyaltyBrand(brandName);
    const email = String(customerEmail || emailFromQuery || "").trim();
    try {
      const res = await loyaltyFetch(
        buildCustomerPath(brand, profileId, "/loyalty/status", email),
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...loyaltyBrandHeaders(brand),
          },
          body: JSON.stringify({
            tierKey,
            ...(tierReason.trim() ? { reason: tierReason.trim() } : {}),
          }),
        },
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || `Erreur HTTP ${res.status}`);
      setTierOpen(false);
      notify("Niveau fidélité mis à jour", "success");
      await fetchAllData();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!brandName) {
    return (
      <Box p={3} minHeight="100vh" bgcolor="#f5f7fa">
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          Veuillez sélectionner une marque pour consulter ce profil.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/fidelite')}>Retour au tableau de bord</Button>
      </Box>
    );
  }

  if (loading || resolvingProfileId) {
    return (
      <Box p={5} display="flex" justifyContent="center" alignItems="center" minHeight="50vh" bgcolor="#f5f7fa">
        <CircularProgress sx={{ color: SECTION_COLORS.tiers.main }} />
      </Box>
    );
  }

  if (error || !customer) {
    return (
      <Box p={3} minHeight="100vh" bgcolor="#f5f7fa">
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/fidelite')}>Retour</Button>
      </Box>
    );
  }

  const applyCouponStatus = async (couponId, status) => {
    if (!brandName || !profileId || !couponId) return;
    setCouponActionId(couponId);
    setSaving(true);
    const brand = normalizeLoyaltyBrand(brandName);
    const email = String(customerEmail || emailFromQuery || "").trim();
    try {
      const res = await loyaltyFetch(
        buildCustomerPath(brand, profileId, `/coupons/${couponId}/status`, email),
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...loyaltyBrandHeaders(brand),
          },
          body: JSON.stringify({ status }),
        },
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(loyaltyApiDetail(data?.detail) || `Erreur HTTP ${res.status}`);
      notify("Lot coupon mis à jour — les rewards suivent en cascade", "success");
      await fetchAllData();
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setSaving(false);
      setCouponActionId(null);
    }
  };

  return (
    <Box p={3} bgcolor="#f5f7fa" minHeight="100vh">
      <Box maxWidth={1200} mx="auto">
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/fidelite')}>
            Retour au tableau de bord
          </Button>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={openEdit}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              Modifier le profil
            </Button>
            <Button
              variant="contained"
              startIcon={<StarIcon />}
              onClick={openTier}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, background: SECTION_COLORS.tiers.gradient }}
            >
              Forcer le niveau
            </Button>
          </Box>
        </Box>

        {/* PROFIL HEADER */}
        <Paper sx={{ p: { xs: 2, md: 4 }, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12}>
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="h4" fontWeight={800} fontFamily="Montserrat, sans-serif" color="#1a1a2e">
                  {profileId}
                </Typography>

                <Box display="flex" flexWrap="wrap" gap={3} mt={1} mb={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Profil ID</Typography>
                    <Typography variant="body2" fontWeight={600} color="#334155">{profileId}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Marque</Typography>
                    <Typography variant="body2" fontWeight={600} color="#334155">{brandName}</Typography>
                  </Box>
                  {customer?.gender && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Genre</Typography>
                      <Typography variant="body2" fontWeight={600} color="#334155">{customer.gender}</Typography>
                    </Box>
                  )}
                  {customer?.birthdate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Naissance</Typography>
                      <Typography variant="body2" fontWeight={600} color="#334155">{new Date(customer.birthdate).toLocaleDateString()}</Typography>
                    </Box>
                  )}
                </Box>

                <Box mt={2}>
                  <LoyaltyStatusOverview
                    loyalty={loyalty}
                    customer={customer}
                    accentColor={SECTION_COLORS.tiers.main}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {shouldShowMergeNotice(reconciliation) && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {getMergeNoticeMessage(reconciliation, {
              email: customerEmail || emailFromQuery || customer?.email,
            })}
          </Alert>
        )}

        <CustomerValidityDatesPanel
          customer={customer}
          movements={movements}
          loyalty={loyalty}
          movementsLoadError={loadErrors.pointMovements}
        />

        {loadErrors.coupons && entitlementsTab === 0 && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            Impossible de charger les coupons : {loadErrors.coupons}
          </Alert>
        )}

        {/* COUPONS + historique entitlements */}
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
          <Typography variant="h6" fontWeight={700} fontFamily="Montserrat, sans-serif" color="#1a1a2e">
            Coupons & récompenses
          </Typography>
          <Tabs
            value={entitlementsTab}
            onChange={(_, v) => setEntitlementsTab(v)}
            sx={{ minHeight: 32, "& .MuiTab-root": { textTransform: "none", minHeight: 32, fontSize: 12, fontWeight: 600 } }}
          >
            <Tab label={`Portefeuille (${customerCouponsWithRewards.length})`} />
            <Tab label="Historique" />
          </Tabs>
        </Box>

        {entitlementsTab === 0 && (
          <>
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Le statut s’applique au <strong>lot coupon</strong> : toutes les rewards liées sont mises à jour ensemble.
              Les actions sont désactivées si le modèle catalogue a été retiré ou le coupon invalidé.
            </Alert>
            <Box sx={{ mb: 4 }}>
              <CustomerCouponsEntitlementsTable
                items={customerCouponsWithRewards}
                rewardDefs={rewardDefs}
                catalogStatusLabels={catalogStatusLabels}
                showActions
                onStatusChange={applyCouponStatus}
                saving={saving}
                couponActionId={couponActionId}
              />
            </Box>
          </>
        )}

        {entitlementsTab === 1 && (
          <Paper sx={{ p: 2, mb: 4, borderRadius: 3, border: "1px solid #e8ecf1" }}>
            <EntitlementsHistoryPanel
              mode="client"
              brandName={brandName}
              profileId={profileId}
              email={customerEmail || emailFromQuery || customer?.email}
            />
          </Paper>
        )}

        <Box sx={{ mb: 4 }}>
          <LoyaltyHistoryPanel
            brandName={brandName}
            profileId={profileId}
            email={customerEmail || emailFromQuery || customer?.email}
            refreshToken={dataRefreshToken}
          />
        </Box>

        {isPurchasesMode(loyalty?.qualificationMode) ? (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight={700} fontFamily="Montserrat, sans-serif" mb={2} color="#1a1a2e">
              Mouvements d’achats ({purchaseMovements.length})
            </Typography>
            {loadErrors.purchaseMovements && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                Impossible de charger le ledger achats — {loadErrors.purchaseMovements}
              </Alert>
            )}
            <PurchaseMovementsTable
              movements={purchaseMovements}
              loadError={loadErrors.purchaseMovements}
            />
          </Box>
        ) : (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight={700} fontFamily="Montserrat, sans-serif" mb={2} color="#1a1a2e">
              Historique des Points ({movements.length})
            </Typography>
            {loadErrors.pointMovements && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                Impossible de charger l'historique pour le moment — {loadErrors.pointMovements}
              </Alert>
            )}
            <PointMovementsTable
              movements={movements}
              loadError={loadErrors.pointMovements}
            />
          </Box>
        )}

        {isPurchasesMode(loyalty?.qualificationMode) && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight={700} fontFamily="Montserrat, sans-serif" mb={1} color="#1a1a2e">
              Portefeuille points (hors qualification palier)
            </Typography>
            <Typography fontSize={12} color="text.secondary" mb={2}>
              Les points n’influencent pas le palier tant que le mode Achats est actif.
            </Typography>
            {loadErrors.pointMovements && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                Impossible de charger l'historique pour le moment — {loadErrors.pointMovements}
              </Alert>
            )}
            <PointMovementsTable
              movements={movements}
              loadError={loadErrors.pointMovements}
            />
          </Box>
        )}

        <Box sx={{ mb: 4 }}>
          <SaleTransactionsPanel
            brandName={brandName}
            profileId={profileId}
            email={customerEmail || emailFromQuery || customer?.email}
            refreshToken={dataRefreshToken}
          />
        </Box>

      </Box>

      {/* ── Edit customer dialog (POST /customers/upsert) ───────────────── */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography fontWeight={800} fontFamily="Montserrat, sans-serif">Modifier le profil</Typography>
          <IconButton onClick={() => !saving && setEditOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Genre"
                value={editForm.gender}
                onChange={(e) => setEditForm((p) => ({ ...p, gender: e.target.value }))}
                size="small"
              >
                <MenuItem value=""><em>Non spécifié</em></MenuItem>
                <MenuItem value="M">Masculin</MenuItem>
                <MenuItem value="F">Féminin</MenuItem>
                <MenuItem value="Other">Autre</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date de naissance"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={editForm.birthdate}
                onChange={(e) => setEditForm((p) => ({ ...p, birthdate: e.target.value }))}
                size="small"
              />
            </Grid>
          </Grid>
          {actionError && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{actionError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={saving} sx={{ textTransform: "none" }}>Annuler</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleUpsertCustomer}
            disabled={saving}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, background: SECTION_COLORS.search?.gradient || SECTION_COLORS.tiers.gradient }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Set tier dialog (PATCH …/loyalty/status) ───────── */}
      <Dialog open={tierOpen} onClose={() => !saving && setTierOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography fontWeight={800} fontFamily="Montserrat, sans-serif">Forcer le niveau</Typography>
          <IconButton onClick={() => !saving && setTierOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Ce changement ajuste le solde de points au minimum du niveau sélectionné (audit “ADMIN_SET_TIER”).
          </Alert>
          <TextField
            fullWidth
            select
            label="Niveau cible"
            value={tierKey}
            onChange={(e) => setTierKey(e.target.value)}
            size="small"
          >
            {tiers.length === 0 ? (
              <MenuItem value=""><em>Aucun niveau disponible</em></MenuItem>
            ) : tiers.map((t) => (
              <MenuItem key={t.key} value={t.key}>
                {t.name} — min {t.minStatusPoints} SP
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Motif (optionnel)"
            value={tierReason}
            onChange={(e) => setTierReason(e.target.value)}
            size="small"
            sx={{ mt: 2 }}
            placeholder="Ex. correction manuelle support"
          />
          {actionError && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{actionError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTierOpen(false)} disabled={saving} sx={{ textTransform: "none" }}>Annuler</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSetTier}
            disabled={saving || !tierKey || tiers.length === 0}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, background: SECTION_COLORS.tiers.gradient }}
          >
            Appliquer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
